import { AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { createPortal } from 'react-dom';
import { springSoft } from '@/animations/variants';
import type { Toast, ToastTone } from '@/contexts/ToastContext';

const ICONS: Record<ToastTone, typeof Info> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

const TONE_CLASS: Record<ToastTone, string> = {
  success: 'text-success',
  error: 'text-danger',
  info: 'text-accent-text',
};

interface ToastViewportProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

export function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  /**
   * Portalled to <body>, so this is a sibling of the modal and the service
   * picker rather than a descendant — nothing can clip it, and painting order
   * comes down to the number below.
   *
   * z-200 is the notification tier: above the modal (100) and the picker (120).
   * A toast usually reports on an action taken *inside* an overlay, so sitting
   * beneath that overlay makes it useless. See the stacking order in
   * globals.css.
   */
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[200] flex flex-col items-center gap-2 p-4"
    >
      <AnimatePresence initial={false}>
        {toasts.map((toast) => {
          const Icon = ICONS[toast.tone];
          return (
            <motion.button
              key={toast.id}
              type="button"
              layout
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              transition={springSoft}
              onClick={() => onDismiss(toast.id)}
              className="pointer-events-auto flex items-center gap-2.5 rounded-xl bg-raised px-4 py-3 text-left shadow-lg ring-hairline"
            >
              <Icon className={`size-4 shrink-0 ${TONE_CLASS[toast.tone]}`} strokeWidth={2.2} />
              <span className="text-[13.5px] text-ink">{toast.message}</span>
            </motion.button>
          );
        })}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
